import {
  Badge,
  BadgeProps,
  Divider,
  List,
  ListItem,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SectionCard } from './section-card'
import { useThreadTopicsQuery } from 'services/thread'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { IThreadTopic } from 'types/threads'
import { ChatBox } from './chat-box'
import { ITender, ITenderNotificationsPoll } from 'types/tender'

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -6,
    top: 2,
  },
}))

type Props = {
  tender: ITender
  pollData?: ITenderNotificationsPoll
}

export const ApplicationChatSection = ({ tender, pollData }: Props) => {
  const { t } = useTranslation()

  const [selectedTopic, setSelectedTopic] = useState<IThreadTopic | null>(null)

  const args = {
    page: 0,
    size: 500,
    tenderId: tender.id,
  } as const

  const { data: threadTopics, isLoading, isError } = useThreadTopicsQuery(args)

  if (isLoading) return <Loading />

  if (isError) return <Error />

  if (threadTopics === undefined) {
    return <></>
  }

  return (
    <SectionCard minHeight={500} title={t('applicationCollection')}>
      <Stack
        direction="row"
        maxHeight={600}
        height="100%"
        spacing={3}
        sx={{ flex: 1 }}
      >
        <Stack spacing={2} flexShrink={0} width="360px">
          <Typography fontWeight="bold">{t('topicsList')}</Typography>
          <Divider flexItem />
          <List sx={{ maxHeight: '100%', overflow: 'auto', padding: 0 }}>
            {threadTopics.items.map((topic) => {
              const isSelected = selectedTopic?.id === topic.id
              const unreadTopic = pollData?.threadTopicCountSummaries?.find(
                (item) => item.tenderTopicId === topic.id
              )
              return (
                <ListItem
                  key={topic.id}
                  onClick={() => setSelectedTopic(topic)}
                  sx={{
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    cursor: 'pointer',
                    background: isSelected ? 'rgba(0, 0, 0, 0.12)' : '',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <StyledBadge
                    badgeContent={unreadTopic ? unreadTopic.count : 0}
                    color="error"
                  >
                    {topic.topicName}
                  </StyledBadge>
                </ListItem>
              )
            })}
          </List>
        </Stack>
        <Divider flexItem orientation="vertical" />
        {selectedTopic && <ChatBox selectedTopic={selectedTopic} />}
      </Stack>
    </SectionCard>
  )
}
